import { NetworkStatus } from "@apollo/client";
import { Button, Spin } from "antd";
import { get, isNil } from "lodash";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import omitDeep from "omit-deep-lodash";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import { fieldRegistryToVariables } from "./catalog";
import View from "./View";

const clean = (value) => (isNil(value) ? null : omitDeep(value, "__typename"));

const Canvas = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const navigate = useNavigate();
  const { id } = useParams();
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.CANVASES.LIST.path);
  const { data, loading, refetch } = useQuery(graphql.queries.PROCESS, {
    variables: { where: { id } },
  });
  const {
    data: draftData,
    loading: draftLoading,
    networkStatus: draftStatus,
    refetch: refetchDraft,
  } = useQuery(graphql.queries.PROCESS_DRAFT, {
    variables: { where: { process: { id } } },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  // Scoping to this process surfaces its custom fields as canvas variables.
  const { data: registryData } = useQuery(graphql.queries.FIELD_REGISTRY, {
    variables: { process: { id } },
  });
  const variables = useMemo(
    () => fieldRegistryToVariables(registryData?.fieldRegistry),
    [registryData],
  );
  const process = useMemo(() => clean(get(data, "process")), [data]);
  const draft = useMemo(
    () => clean(get(draftData, "processDraft")),
    [draftData],
  );
  const reload = useCallback(
    () =>
      Promise.all([refetch(), refetchDraft()]).then(
        ([processResult, draftResult]) => ({
          process: clean(get(processResult, "data.process")),
          draft: clean(get(draftResult, "data.processDraft")),
        }),
      ),
    [refetch, refetchDraft],
  );
  const draftPending =
    draftStatus === NetworkStatus.loading ||
    draftStatus === NetworkStatus.setVariables;
  const draftFailed = draftStatus === NetworkStatus.error;
  const draftMatches = isNil(draft) || get(draft, "process.id") === id;
  const ready =
    !isNil(process) &&
    process.id === id &&
    !isNil(draftData) &&
    !draftPending &&
    !draftFailed &&
    draftMatches;
  if (!ready && (loading || draftLoading))
    return (
      <div className="canvas-editor__centered">
        <Spin tip={t("FORM.LOADING", "Loading…")} />
      </div>
    );
  if (!ready)
    return (
      <div className="canvas-editor__centered">
        <p>{t("CANVAS_EDITOR.LOAD_ERROR")}</p>
        <Button onClick={() => reload().catch(() => null)}>
          {t("CANVAS_EDITOR.LOAD_RETRY")}
        </Button>
      </div>
    );
  return (
    <View
      key={id}
      process={process}
      draft={draft}
      variables={variables}
      cancel={cancel}
      reload={reload}
    />
  );
};

export default Canvas;
